import { Navbar } from '@/components/feature/Navbar';
import { Footer } from '@/components/feature/Footer';

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/bc69cc2a78585796b21c91916f015b55.jpg"
            alt="ZEPHYRION LLC"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
          <div className="relative z-10 text-center px-6">
            <p className="text-amber-400 text-sm font-medium tracking-widest uppercase mb-3">Phoenix, Arizona</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              About ZEPHYRION LLC
            </h1>
            <p className="text-white/75 text-lg max-w-xl mx-auto">Crafting Excellence in Kitchen & Dining Since 2020</p>
          </div>
        </section>

        {/* Story */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <p className="text-amber-600 text-sm font-medium tracking-wider uppercase mb-2">Our Story</p>
              <h2 className="text-4xl font-bold text-stone-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Born from a Passion for Cooking
              </h2>
            </div>
            <div className="prose prose-stone max-w-none space-y-6 text-stone-600 text-lg leading-relaxed">
              <p>
                ZEPHYRION LLC was founded in Phoenix, Arizona with a singular mission: to bring professional-grade kitchen and dining products to home cooks who refuse to compromise on quality. We believe that the tools you cook with matter — they affect not just the food you make, but the joy you feel while making it.
              </p>
              <p>
                Our name, inspired by Zephyrus — the Greek god of the west wind — reflects our Phoenix roots and our belief in the gentle, transformative power of great cooking. Just as a warm breeze can change the atmosphere of a room, the right kitchen tools can transform an ordinary meal into an extraordinary experience.
              </p>
              <p>
                Every product in our collection is hand-selected by our team of culinary enthusiasts and professional chefs. We test each item rigorously in real kitchens, evaluating performance, durability, and design before it earns a place in our catalog. We partner only with manufacturers who share our commitment to quality craftsmanship and sustainable practices.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
              <img
                src="https://readdy.ai/api/search-image?query=premium%20cookware%20being%20tested%20in%20professional%20kitchen%2C%20chef%20inspecting%20quality%2C%20warm%20lighting%2C%20high-end%20kitchen%20environment&width=700&height=500&seq=about2&orientation=landscape"
                alt="Quality Testing"
                className="w-full h-64 object-cover object-top rounded-2xl"
              />
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20kitchen%20and%20dining%20product%20display%20showroom%2C%20premium%20tableware%20and%20cookware%20arranged%20beautifully%2C%20warm%20ambient%20lighting&width=700&height=500&seq=about3&orientation=landscape"
                alt="Our Products"
                className="w-full h-64 object-cover object-top rounded-2xl"
              />
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 bg-stone-50">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>Our Values</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-award-line', title: 'Quality First', desc: 'We never compromise on materials, craftsmanship, or performance. Every product must meet our exacting standards.' },
                { icon: 'ri-heart-line', title: 'Customer Love', desc: 'Our customers are at the heart of everything we do. Your satisfaction is our greatest achievement.' },
                { icon: 'ri-leaf-line', title: 'Sustainability', desc: 'We source responsibly and partner with eco-conscious manufacturers to minimize our environmental footprint.' },
                { icon: 'ri-lightbulb-line', title: 'Innovation', desc: 'We continuously seek out the latest innovations in kitchen technology and design to bring you the best.' },
              ].map(v => (
                <div key={v.title} className="bg-white rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-2xl mx-auto mb-4">
                    <i className={`${v.icon} text-amber-600 text-2xl`} />
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-2">{v.title}</h3>
                  <p className="text-stone-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <div className="bg-stone-900 rounded-3xl p-10 text-center">
              <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Get in Touch</h2>
              <p className="text-stone-400 mb-8">We'd love to hear from you. Reach out with questions, feedback, or just to say hello.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: 'ri-map-pin-line', label: 'Address', value: '4325 E Waltann Ln, Phoenix, AZ 85032' },
                  { icon: 'ri-mail-line', label: 'Email', value: 'zephyrionllc@outlook.com' },
                  { icon: 'ri-time-line', label: 'Hours', value: 'Mon–Fri: 9AM–6PM MST' },
                ].map(c => (
                  <div key={c.label} className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-xl">
                      <i className={`${c.icon} text-amber-400`} />
                    </div>
                    <p className="text-stone-400 text-xs uppercase tracking-wider">{c.label}</p>
                    <p className="text-white text-sm text-center">{c.value}</p>
                  </div>
                ))}
              </div>
              <a
                href="mailto:zephyrionllc@outlook.com"
                className="inline-flex items-center gap-2 mt-8 bg-amber-500 hover:bg-amber-600 text-white px-8 py-3.5 rounded-xl font-semibold transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-send-line" /> Send Us an Email
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
